import React, { useEffect, useState } from "react";
import { Star, Check, X, Trash2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

function Stars({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={14}
          className={s <= value ? "fill-[#CFF048] text-[#CFF048]" : "text-[#27272A]"}
        />
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const s = status || "pending";
  const map = {
    pending: { bg: "bg-yellow-500/15", fg: "text-yellow-400", border: "border-yellow-500/30" },
    approved: { bg: "bg-[#CFF048]/15", fg: "text-[#CFF048]", border: "border-[#CFF048]/40" },
    rejected: { bg: "bg-red-500/15", fg: "text-red-400", border: "border-red-500/30" },
  };
  const c = map[s] || map.pending;
  return (
    <span
      className={`label-mono ${c.bg} ${c.fg} ${c.border} border px-2 py-0.5 text-[10px] uppercase`}
      data-testid={`review-status-${s}`}
    >
      {s}
    </span>
  );
}

export default function AdminReviews() {
  const [tab, setTab] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [list, ct] = await Promise.all([
        api.get(`/admin/reviews?status=${tab}`),
        api.get("/admin/reviews/counts"),
      ]);
      setReviews(list.data || []);
      setCounts(ct.data || { pending: 0, approved: 0, rejected: 0 });
    } catch {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const act = async (id, action) => {
    setActingId(id);
    try {
      if (action === "delete") {
        await api.delete(`/admin/reviews/${id}`);
        toast.success("REVIEW DELETED");
      } else {
        await api.post(`/admin/reviews/${id}/${action}`);
        toast.success(action === "approve" ? "REVIEW APPROVED — NOW LIVE" : "REVIEW REJECTED");
      }
      await load();
    } catch (err) {
      toast.error("Action failed", { description: err?.response?.data?.detail || "" });
    } finally {
      setActingId("");
    }
  };

  return (
    <AdminLayout title="Reviews · Moderation">
      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="reviews-stats">
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// PENDING</p>
          <p className="font-display font-black text-2xl text-yellow-400 mt-1">{counts.pending}</p>
        </div>
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// APPROVED · LIVE</p>
          <p className="font-display font-black text-2xl text-[#CFF048] mt-1">{counts.approved}</p>
        </div>
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// REJECTED</p>
          <p className="font-display font-black text-2xl text-red-400 mt-1">{counts.rejected}</p>
        </div>
        <button
          onClick={load}
          className="ml-auto border border-[#27272A] px-3 py-2 label-mono hover:border-[#CFF048] hover:text-[#CFF048] flex items-center gap-2"
          data-testid="reviews-refresh-btn"
        >
          <RefreshCcw size={14} /> REFRESH
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#27272A]" data-testid="reviews-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 label-mono uppercase transition-colors -mb-px border-b-2 ${
              tab === t.id
                ? "text-[#CFF048] border-[#CFF048]"
                : "text-[#71717A] border-transparent hover:text-white"
            }`}
            data-testid={`reviews-tab-${t.id}`}
          >
            {t.label}
            {t.id === "pending" && counts.pending > 0 && (
              <span className="ml-2 bg-[#CFF048] text-black text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm">
                {counts.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center label-mono text-[#71717A] py-20">// LOADING REVIEWS...</div>
      ) : reviews.length === 0 ? (
        <div className="border border-[#27272A] p-12 text-center label-mono text-[#71717A]" data-testid="reviews-empty">
          // NO {tab.toUpperCase()} REVIEWS
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map((r) => {
            const status = r.status || "pending";
            const isBusy = actingId === r.id;
            return (
              <div
                key={r.id}
                className="border border-[#27272A] p-5 bg-[#0A0A0A]"
                data-testid={`admin-review-${r.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Stars value={r.rating} />
                      <StatusPill status={status} />
                      <span className="label-mono text-[#71717A]">
                        // {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-3 label-mono text-[#71717A]">
                      PRODUCT: <span className="text-white">{r.product_name || r.product_id}</span>
                    </p>
                    <p className="label-mono text-[#71717A]">
                      BY: <span className="text-white">{r.user_name || "Anonymous"}</span>
                      {r.user_email ? <span className="text-[#71717A]"> · {r.user_email}</span> : null}
                    </p>
                    {r.title && (
                      <p className="font-display font-bold mt-3 text-white">{r.title}</p>
                    )}
                    <p className="text-[#A1A1AA] text-sm mt-2 leading-relaxed whitespace-pre-wrap">
                      {r.comment}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 min-w-[150px]">
                    {status !== "approved" && (
                      <button
                        onClick={() => act(r.id, "approve")}
                        disabled={isBusy}
                        className="bg-[#CFF048] text-black font-mono font-bold text-xs px-3 py-2 flex items-center justify-center gap-2 hover:bg-white transition-colors disabled:opacity-50"
                        data-testid={`approve-review-${r.id}`}
                      >
                        <Check size={14} /> APPROVE
                      </button>
                    )}
                    {status !== "rejected" && (
                      <button
                        onClick={() => act(r.id, "reject")}
                        disabled={isBusy}
                        className="border border-[#27272A] text-white font-mono font-bold text-xs px-3 py-2 flex items-center justify-center gap-2 hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50"
                        data-testid={`reject-review-${r.id}`}
                      >
                        <X size={14} /> REJECT
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm("Delete this review permanently?")) act(r.id, "delete");
                      }}
                      disabled={isBusy}
                      className="border border-[#27272A] text-[#71717A] font-mono font-bold text-xs px-3 py-2 flex items-center justify-center gap-2 hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50"
                      data-testid={`delete-review-${r.id}`}
                    >
                      <Trash2 size={14} /> DELETE
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
